<script setup lang="ts">
import type { WikiItem, WikiVehiclePort } from "~~/types/gamedata";
import type { ComponentSuperGroup } from "~/utils/build-ship-component-groups";

const props = defineProps<{
  ports: WikiVehiclePort[];
  items: Map<string, WikiItem>;
  /** z.B. "Orig 100i" — für generische Namen wie das Frachtraster ("Orig 100i Cargogrid"). */
  shipLabel?: string;
  loading?: boolean;
}>();

/** Baut die zweistufige Gruppierung (Super-Karte → Unter-Abschnitt → Zeilen) aus den Ports. */
const superGroups = computed(() => buildComponentSuperGroups(props.ports, props.items, props.shipLabel));

const columns = computed(() =>
  COMPONENT_COLUMNS.map((superLabels) =>
    superLabels
      .map((label) => superGroups.value.find((group) => group.label === label))
      .filter((group): group is ComponentSuperGroup => !!group),
  ).filter((column) => column.length),
);
</script>

<template>
  <div v-if="loading" class="flex items-center gap-2 text-sm text-neutral-400">
    <UIcon name="i-lucide-loader-2" class="size-4 animate-spin" />
    Lade Ausrüstung…
  </div>
  <p v-else-if="!columns.length" class="text-sm text-neutral-500">
    Keine Ausrüstungsdaten verfügbar.
  </p>
  <div v-else class="grid grid-cols-1 gap-4 lg:grid-cols-3">
    <div v-for="(column, columnIndex) in columns" :key="columnIndex" class="flex flex-col gap-4">
      <ShipexkursStatCard v-for="group in column" :key="group.label" :title="group.label">
        <div class="space-y-4">
          <div v-for="subGroup in group.subGroups" :key="subGroup.label">
            <p class="mb-1.5 flex items-center gap-1.5 text-[0.65rem] font-medium text-neutral-500 uppercase tracking-wide">
              <ShipexkursSystemIcon :name="subGroup.icon" class="size-3.5 shrink-0" />
              {{ subGroup.label }}
            </p>
            <div class="space-y-1.5">
              <ShipexkursComponentRowItem
                v-for="entry in subGroup.rows"
                :key="entry.key"
                :row="entry"
              />
            </div>
          </div>
        </div>
      </ShipexkursStatCard>
    </div>
  </div>
</template>
